import { createAsyncThunk } from "@reduxjs/toolkit";
import type { NavigateFunction } from "react-router-dom";
import {
  addChildToObject,
  createStorageObject,
  deleteChildObject,
  deleteStorageObject,
  getAllStorageObjects,
  updateStorageObject,
} from "../../../services/api/instances/storageApi/methods";
import type { StylistValue } from "../../../services/api/instances/stylistApi/stylistTypes";
import {
  createThread,
  getMessages,
  postCompMessage,
} from "../../../services/api/methods";
import type { Message, MessageList } from "../../../services/api/methodsTypes";
import i18n from "../../../services/i18n";
import { createStorageObjectApi } from "../../../services/lib/storageObjectManager";
import { getServicePagePath } from "../../../services/navigation/navigateToRoute";
import type { RootState } from "../../../services/redux/types";
import {
  getCountOfFolders,
  getCountOfObjects,
} from "../../../shared/utils/objectStorageFunctions";
import { setSelectedStylistAction, setStylistAction } from "./slice";
import { AssistantValue } from "../../../services/api/instances/assistantsApi/types";
import { StorageObject } from "../../../services/api/instances/storageApi/types/object_types";
import { getError } from "../../../services/lib/getError";

export const getStylistAssistantsAction = createAsyncThunk(
  "stylist/getAssistants",
  async (_, { rejectWithValue }) => {
    try {
      return await getAllStorageObjects<AssistantValue>("assistants");
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const createStylistThreadAction = createAsyncThunk(
  "stylist/createThread",
  async (_, { rejectWithValue }) => {
    try {
      return await createThread();
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getStylistsAction = createAsyncThunk(
  "stylist/getAllStylists",
  async (_, { rejectWithValue }) => {
    try {
      return await getAllStorageObjects<StylistValue>("stylizator");
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const createStylistAction = createAsyncThunk(
  "stylist/createStylist",
  async (
    {
      isFolder,
      name,
      withoutCount,
    }: { isFolder: "object" | "folder"; name?: string; withoutCount?: boolean },
    thunkAPI,
  ) => {
    try {
      const { stylists } = (thunkAPI.getState() as RootState).stylistState;
      const defaultName =
        isFolder === "folder"
          ? name
            ? name
            : i18n.t("stylist.default_folder_name")
          : i18n.t("stylist.default_name");
      const count =
        isFolder === "folder"
          ? getCountOfFolders(stylists)
          : getCountOfObjects(stylists);
      let res: any;
      console.log("Name", name);
      if (isFolder === "object") {
        res = await thunkAPI.dispatch(createStylistThreadAction()).unwrap();
      }

      const stylist = {
        friendly_name: !withoutCount
          ? `${defaultName} ${count + 1}`
          : `${defaultName}`,
        value:
          isFolder === "folder"
            ? "null"
            : JSON.stringify({
                stylist_object: {
                  prompt: "",
                  result: "",
                },
              }),
      };

      return await createStorageObject<StylistValue>(stylist, "stylizator");
    } catch (e: unknown) {
      const message = getError(e);
      return thunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const updateStylistAction = createAsyncThunk(
  "stylist/updateStylist",
  async (postObj: StorageObject<StylistValue>, { rejectWithValue }) => {
    try {
      return await updateStorageObject<StylistValue>(postObj, "stylizator");
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const updateStylistWithChildrenAction = createAsyncThunk(
  "stylist/updateStylistWithChildren",
  async (
    body: { childId: string | null; parentId: string },
    { rejectWithValue },
  ) => {
    try {
      const { childId, parentId } = body;

      return await addChildToObject(childId as string, parentId, "stylizator");
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const deleteChildrenAction = createAsyncThunk(
  "stylist/deleteStylistChildren",
  async (body: { childId: string; parentId: string }, { rejectWithValue }) => {
    try {
      const { childId, parentId } = body;
      return await deleteChildObject(childId, parentId);
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const deleteStylistAction = createAsyncThunk(
  "stylist/deleteStylist",
  async (body: { id: string; navigate: NavigateFunction }, thunkAPI) => {
    try {
      const { navigate, id } = body;
      const { stylists, selectedStylist } = (thunkAPI.getState() as RootState)
        .stylistState;
      const storage = createStorageObjectApi<StylistValue>(stylists);
      const folder = storage.findParent(id);

      if (folder !== null) {
        const nextFolderStylist = folder.children?.filter(
          (dialog: StorageObject<StylistValue>) => dialog.id !== id,
        ) as StorageObject<StylistValue>[];
        const lastFolderStylist = nextFolderStylist?.at(-1);
        const updatedStylists = storage.updateById(
          folder.id,
          {
            children: [...(nextFolderStylist as StorageObject<StylistValue>[])],
          },
          true,
        );
        thunkAPI.dispatch(
          setStylistAction(updatedStylists as StorageObject<StylistValue>[]),
        );

        if (!nextFolderStylist[0]) {
          navigate(getServicePagePath(folder.id!, "stylizator"));
          thunkAPI.dispatch(setSelectedStylistAction(folder));
        } else if (selectedStylist?.id === id) {
          thunkAPI.dispatch(
            setSelectedStylistAction(
              lastFolderStylist as StorageObject<StylistValue>,
            ),
          );
          navigate(getServicePagePath(lastFolderStylist?.id!, "stylizator"));
        }
      }

      if (folder === null) {
        const nextStylist = storage.removeById(id);
        const lastStylist = nextStylist[nextStylist.length - 1];

        thunkAPI.dispatch(setStylistAction(nextStylist));

        if (!nextStylist[0]) {
          navigate(getServicePagePath(null, "stylizator"));
          thunkAPI.dispatch(setSelectedStylistAction(null));
          thunkAPI.dispatch(setStylistAction([]));
        } else if (selectedStylist?.id === id) {
          thunkAPI.dispatch(setSelectedStylistAction(lastStylist));
          navigate(getServicePagePath(lastStylist.id!, "stylizator"));
        }
      }

      return await deleteStorageObject(id, "stylizator");
    } catch (e: unknown) {
      const message = getError(e);
      return thunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const postStylistMessageAction = createAsyncThunk(
  "stylist/postCompMessage",
  async (message: Message, thunkAPI) => {
    try {
      const { thread } = (thunkAPI.getState() as RootState).stylistState;

      return await postCompMessage(thread?.id!, message);
    } catch (e: unknown) {
      const message = getError(e);
      return thunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getStylistMessagesAction = createAsyncThunk(
  "stylist/getMessages",
  async (_, thunkAPI) => {
    try {
      const { thread } = (thunkAPI.getState() as RootState).stylistState;

      const res: MessageList = await getMessages(thread?.id!);
      const newMessageList: MessageList = {
        ...res,
        data: res.data,
      };

      return newMessageList;
    } catch (e: unknown) {
      const message = getError(e);
      return thunkAPI.rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);
